import { domain } from "@relode/core";
import { format } from "date-fns";

import { converters } from ".";
import { JobPublicDetails } from "../../types";
import { JobPublicMarketResearchResponse } from "../controllers";
import {
    CustomTextJobQualificationResponse,
    CustomYesNoJobQualificationResponse,
    EducationJobQualificationResponse,
    LicenseJobQualificationResponse,
    SkillJobQualificationResponse,
} from "../controllers/jobs";
import { convertJobManagerContactResponse } from "./convertCommonResponses";

const convertDate = (date: string) => {
    const _date = new Date(date);
    return format(_date, "MM/dd/yyyy");
};

export const convertJobPublicMarketResearchResponse = (
    res: JobPublicMarketResearchResponse
) => {
    if (!res) {
        console.error(
            "attempted to convert JobPublicMarketResearchResponse, but it is null"
        );
        return null;
    }

    const result: JobPublicDetails = {
        canAccessJobMarketResearch: res.canAccessJobMarketResearch,
        canUpsellMarketResearch: res.canUpsellMarketResearch,
        isSharedFromAdmin: res.isSharedFromAdmin,
        qualifications: {
            educationJobQualifications:
                res.jobQualifications.educationJobQualifications?.map(x =>
                    convertEducationJobQualificationResponse(x)
                ),
            skillJobQualifications:
                res.jobQualifications?.skillJobQualifications?.map(x =>
                    convertSkillJobQualificationResponse(x)
                ),
            skillAnyOfJobQualifications:
                res.jobQualifications?.skillAnyOfJobQualifications?.map(x =>
                    convertSkillJobQualificationResponse(x)
                ),
            licenseJobQualifications:
                res.jobQualifications?.licenseJobQualifications?.map(x =>
                    convertLicenseJobQualificationResponse(x)
                ),
            licenseAnyOfJobQualifications:
                res.jobQualifications?.licenseAnyOfJobQualifications?.map(x =>
                    convertLicenseAnyOfJobQualificationResponse(x)
                ),
            customYesNoJobQualifications:
                res.jobQualifications?.customYesNoJobQualifications?.map(x =>
                    convertCustomYesNoJobQualificationResponse(x)
                ),
            customTextJobQualifications:
                res.jobQualifications?.customTextJobQualifications?.map(x =>
                    convertCustomTextJobQualificationResponse(x)
                ),
        },
        details: {
            jobManager: convertJobManagerContactResponse(
                res.jobManagerContactResponse
            ),
            contractEndDate: convertDate(res.contractEndDate),
            contractStartDate: convertDate(res.contractStartDate),
            hoursPerWeek: res.hoursPerWeek,
            jobId: res.jobId,
            jobName: res.jobName,
            jobStatus: domain.JobStatus.fromId(res.jobStatusTypeId),
            jobType: domain.JobType.fromId(res.jobTypeId),
            jobStatusReasonType: domain.JobStatusReasonType.fromId(
                res.jobStatusReasonTypeId
            ),
            jobStatusMessage: res.jobStatusMessage,
            statusReasonTimestamp: res.statusReasonTimestamp,
            locationName: res.locationName,
            openPositions: res.openPositions,
            overview: res.overview,
            teamName: res.companyName,
            primaryJobPosition: converters.convertJobPositionResponse(
                res.primaryPosition
            ),
            secondaryJobPositions: converters.convertJobPositionResponseList(
                res.secondaryPositions
            ),
            benefits: converters.convertTagTypeResponseList(res.benefits),
        },
    };

    return result;
};

const convertCustomYesNoJobQualificationResponse = (
    res: CustomYesNoJobQualificationResponse
) => {
    const qual: domain.CustomYesNoJobQualification = {
        id: res.jobQualificationId,
        isRequired: res.isRequired,
        qualification: domain.Qualification.fromId(res.qualificationId),
        customYesNoChoiceId: res.customYesNoChoiceId,
        yesNoTag: domain.YesNoTag.fromId(res.yesNoTagId),
        customText: res.customQualificationText,
    };

    return qual;
};

const convertCustomTextJobQualificationResponse = (
    res: CustomTextJobQualificationResponse
) => {
    const qual: domain.CustomTextJobQualification = {
        id: res.jobQualificationId,
        isRequired: res.isRequired,
        qualification: domain.Qualification.fromId(res.qualificationId),
        customText: res.customQualificationText,
    };

    return qual;
};


const convertSkillJobQualificationResponse = (
    res: SkillJobQualificationResponse
): domain.SkillJobQualification => {
    const qual: domain.SkillJobQualification = {
        id: res.jobQualificationId, // res.qualificationId,
        isRequired: res.isRequired,
        qualification: domain.Qualification.fromId(res.qualificationId),
        yearsExperience: domain.YearsExperience.fromId(
            res.yearsOfExperienceTagId
        ),
        skills: res.skillChoices.map(c => ({
            choiceId: c.skillChoiceId,
            skillTagId: c.skillTagId,
            skillTagName: c.skillTagName,
            skillCustomText: c.skillCustomText,
        })),
    };
    return qual;
};

const convertLicenseJobQualificationResponse = (
    res: LicenseJobQualificationResponse
) => {
    const qual: domain.LicenseJobQualification = {
        id: res.jobQualificationId,
        licenses: res.licenseChoices.map(c => ({
            choiceId: c.licenseChoiceId,
            licenseTagId: c.licenseTagId,
            licenseTagName: c.licenseTagName,
            licenseCustomText: c.licenseCustomText,
            stateTagId: c.stateTagId,
            stateTagName: c.stateTagName,
        })),
        isRequired: res.isRequired,
        qualification: domain.Qualification.fromId(res.qualificationId),
    };
    return qual;
};

const convertLicenseAnyOfJobQualificationResponse = (
    res: LicenseJobQualificationResponse
) => {
    const qual: domain.LicenseAnyOfJobQualification = {
        id: res.jobQualificationId,
        licenses: res.licenseChoices.map(c => ({
            choiceId: c.licenseChoiceId,
            licenseTagId: c.licenseTagId,
            licenseTagName: c.licenseTagName,
            licenseCustomText: c.licenseCustomText,
            stateTagId: c.stateTagId,
            stateTagName: c.stateTagName,
        })),
        isRequired: res.isRequired,
        qualification: domain.Qualification.fromId(res.qualificationId),
    };
    return qual;
};

const convertEducationJobQualificationResponse = (
    res: EducationJobQualificationResponse
): domain.EducationJobQualification => {
    const qual: domain.EducationJobQualification = {
        id: res.jobQualificationId,
        isRequired: res.isRequired,
        qualification: domain.Qualification.fromId(res.qualificationId),
        education: {
            educationChoiceTagId: res.educationTagId,
            educationChoiceTagName: res.educationTagName,
            educationChoiceId: res.educationChoiceId,
        },
    };
    return qual;
};
